import * as z from "zod";
import { userFacingError } from "../authorization";
import { isUniqueConstraintViolation } from "../dbErrors";
import { zql } from "../generatedDrizzleSchema";
import { fetchCustomRole } from "../rbac/grantResources";
import { BUILTIN_ROLE_IDS, type BuiltinRoleId } from "../rbac/roles";
import { assertValidSpaceDefaultJoinRole } from "../rbac/validation";
import { assertValidSlug } from "../slug";
import { USER_ASSIGNABLE_SPACE_MODES } from "../spaceMode";
import {
  assertNameAvailable,
  assertNotPersonal,
  assertSlugAvailable,
  spaceNameField,
  spaceSlugField,
} from "../spaceName";
import { mutator } from "./mutator";

export const updateSpace = mutator({
  schema: z.object({
    spaceId: z.string(),
    name: spaceNameField.optional(),
    slug: spaceSlugField.optional(),
    mode: z.enum(USER_ASSIGNABLE_SPACE_MODES).optional(),
    defaultJoinBuiltinRoleId: z.enum(BUILTIN_ROLE_IDS).nullable().optional(),
    defaultJoinCustomRoleId: z.string().nullable().optional(),
    blockSelfApproval: z.boolean().optional(),
  }),
  resolve: {
    space: { kind: "space", from: "spaceId" },
    defaultJoinCustomRole: {
      kind: "customRole",
      from: "defaultJoinCustomRoleId",
      optional: true,
    },
  },
  permissions: [
    {
      permission: "space:manage",
      scopeId: ({ args }) => args.space.id,
    },
  ],
  fn: async ({
    tx,
    ctx,
    args: {
      name,
      slug,
      mode,
      defaultJoinBuiltinRoleId,
      defaultJoinCustomRoleId,
      defaultJoinCustomRole,
      blockSelfApproval,
      space,
    },
  }) => {
    assertNotPersonal(space, "update");
    if (slug !== undefined) assertValidSlug(slug);

    if (name !== undefined || slug !== undefined) {
      const existing = await tx.run(zql.spaces.where("tenantId", ctx.tenantId));
      if (name !== undefined) assertNameAvailable(existing, name, space.id);
      if (slug !== undefined) assertSlugAvailable(existing, slug, space.id);
    }

    if (slug !== undefined && slug !== space.slug) {
      ctx.recordAuditMetadata?.({ slug: { from: space.slug, to: slug } });
    }

    if (
      blockSelfApproval !== undefined &&
      blockSelfApproval !== space.blockSelfApproval
    ) {
      ctx.recordAuditMetadata?.({
        blockSelfApproval: {
          from: space.blockSelfApproval,
          to: blockSelfApproval,
        },
      });
    }

    if (
      defaultJoinBuiltinRoleId !== undefined ||
      defaultJoinCustomRoleId !== undefined
    ) {
      const effectiveBuiltin: BuiltinRoleId | null =
        defaultJoinBuiltinRoleId !== undefined
          ? defaultJoinBuiltinRoleId
          : ((space.defaultJoinBuiltinRoleId as BuiltinRoleId | null) ?? null);
      const effectiveCustom: string | null =
        defaultJoinCustomRoleId !== undefined
          ? defaultJoinCustomRoleId
          : (space.defaultJoinCustomRoleId ?? null);

      if (effectiveBuiltin && effectiveCustom) {
        throw userFacingError(
          "A space can have only one default join role at a time"
        );
      }

      const customRole = effectiveCustom
        ? defaultJoinCustomRoleId === undefined
          ? await fetchCustomRole(tx, effectiveCustom, ctx.tenantId)
          : defaultJoinCustomRole!
        : null;
      assertValidSpaceDefaultJoinRole({
        builtinRoleId: effectiveBuiltin,
        customRole,
      });
    }

    try {
      await tx.mutate.spaces.update({
        id: space.id,
        ...(name !== undefined && { name }),
        ...(slug !== undefined && { slug }),
        ...(mode !== undefined && { mode }),
        ...(defaultJoinBuiltinRoleId !== undefined && {
          defaultJoinBuiltinRoleId,
        }),
        ...(defaultJoinCustomRoleId !== undefined && {
          defaultJoinCustomRoleId,
        }),
        ...(blockSelfApproval !== undefined && { blockSelfApproval }),
      });
    } catch (err) {
      if (
        name !== undefined &&
        isUniqueConstraintViolation(err, "spaces_tenant_name_unique")
      ) {
        throw userFacingError("A space with this name already exists");
      }
      if (
        slug !== undefined &&
        isUniqueConstraintViolation(err, "spaces_tenant_slug_unique")
      ) {
        throw userFacingError("This space URL is already taken");
      }
      throw err;
    }
  },
});
